import React, {useCallback, useMemo} from 'react';
import {FormattedMessage} from 'react-intl';

import {matchPath, useLocation} from 'react-router-dom';

import {useAppDispatch, useAppSelector} from 'stores/redux_store';

import {trackEvent as trackEventAction} from 'actions/telemetry_actions';
import {setProductMenuSwitcherOpen} from 'actions/views/product_menu';
import {setStatusDropdown} from 'actions/views/status_dropdown';
import {
    AutoTourStatus,
    FINISHED,
    OnboardingTourSteps,
    TTNameMapToATStatusKey,
    TutorialTourName,
} from 'components/onboarding_tour';
import {savePreferences} from 'mattermost-redux/actions/preferences';
import {getCurrentUserId} from 'mattermost-redux/selectors/entities/common';

import {get, makeGetCategory} from 'mattermost-redux/selectors/entities/preferences';

import {browserHistory} from 'utils/browser_history';
import {
    openInvitationsModal,
    setShowOnboardingCompleteProfileTour,
    setShowOnboardingVisitConsoleTour,
    switchToChannels,
} from 'actions/views/onboarding_tasks';

import {Constants} from 'utils/constants';

import LocalStorageStore from 'stores/local_storage_store';

import {generateTelemetryTag} from './utils';
import {OnboardingTaskCategory, OnboardingTaskList, OnboardingTasksName, TaskNameMapToSteps} from './constants';

const getCategory = makeGetCategory();

const taskLabels = {
    [OnboardingTasksName.CHANNELS_TOUR]: (
        <FormattedMessage
            id='onboardingTask.checklist.task_channels_tour'
            defaultMessage='Take a tour of channels'
        />
    ),
    [OnboardingTasksName.BOARDS_TOUR]: (
        <FormattedMessage
            id='onboardingTask.checklist.task_boards_tour'
            defaultMessage='Manage tasks with your first board'
        />),
    [OnboardingTasksName.PLAYBOOKS_TOUR]: (
        <FormattedMessage
            id='onboardingTask.checklist.task_playbooks_tour'
            defaultMessage='Explore workflows with your first Playbook'
        />
    ),
    [OnboardingTasksName.INVITE_PEOPLE]: (
        <FormattedMessage
            id='onboardingTask.checklist.task_invite'
            defaultMessage='Invite team members to the workspace'
        />
    ),
    [OnboardingTasksName.COMPLETE_YOUR_PROFILE]: (
        <FormattedMessage
            id='onboardingTask.checklist.task_complete_profile'
            defaultMessage='Complete your profile'
        />
    ),
    [OnboardingTasksName.DOWNLOAD_APP]: (
        <FormattedMessage
            id='onboardingTask.checklist.task_download_apps'
            defaultMessage='Download the Desktop and Mobile Apps'
        />
    ),
    [OnboardingTasksName.VISIT_SYSTEM_CONSOLE]: (
        <FormattedMessage
            id='onboardingTask.checklist.task_system_console'
            defaultMessage='Visit the System Console to configure your workspace'
        />
    ),
};

export const useTasksList = () => {
    const pluginsList = useAppSelector((state) => state.plugins.plugins);

    const list: Record<string, string> = {...OnboardingTasksName};
    const pluginsPreferenceState = useAppSelector((state) => get(state, Constants.Preferences.ONBOARDING, 'use_case'));
    const pluginsPreference = pluginsPreferenceState && JSON.parse(pluginsPreferenceState);
    if ((pluginsPreference && !pluginsPreference.boards) || !pluginsList.focalboard) {
        delete list.BOARDS_TOUR;
    }
    if ((pluginsPreference && !pluginsPreference.playbooks) || !pluginsList.playbooks) {
        delete list.PLAYBOOKS_TOUR;
    }

    return Object.values(list);
};

export const useTasksListWithStatus = () => {
    const dataInDb = useAppSelector((state) => getCategory(state, OnboardingTaskCategory));
    const tasksList = useTasksList();
    return useMemo(() =>
        tasksList.map((task) => {
            const status = dataInDb.find((pref) => pref.name === task)?.value;
            return {
                name: task,
                status: status === FINISHED.toString(),
                label: taskLabels[task],
            };
        }), [dataInDb, tasksList]);
};

export const useHandleOnBoardingTaskData = () => {
    const dispatch = useAppDispatch();
    const currentUserId = useAppSelector(getCurrentUserId);
    const storeSavePreferences = useCallback(
        (taskCategory: string, taskName, step: number) => {
            const preferences = [
                {
                    user_id: currentUserId,
                    category: taskCategory,
                    name: taskName,
                    value: step.toString(),
                },
            ];
            dispatch(savePreferences(currentUserId, preferences));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentUserId],
    );

    return useCallback((
        taskName: string,
        step: number,
        trackEvent = true,
        trackEventSuffix?: string,
        taskCategory = OnboardingTaskCategory,
    ) => {
        storeSavePreferences(taskCategory, taskName, step);
        if (trackEvent) {
            const eventSuffix = trackEventSuffix ? `${step}--${trackEventSuffix}` : step.toString();
            const telemetryTag = generateTelemetryTag(OnboardingTaskCategory, taskName, eventSuffix);
            trackEventAction(OnboardingTaskCategory, telemetryTag);
        }
    }, [storeSavePreferences]);
};

export const useHandleOnBoardingTaskTrigger = () => {
    const dispatch = useAppDispatch();
    const handleSaveData = useHandleOnBoardingTaskData();
    const currentUserId = useAppSelector(getCurrentUserId);
    const {pathname} = useLocation();
    const inAdminConsole = matchPath(pathname, {path: '/admin_console'}) != null;
    const inChannels = matchPath(pathname, {path: '/:team/channels/:chanelId'}) != null;

    return (taskName: string) => {
        switch (taskName) {
        case OnboardingTasksName.CHANNELS_TOUR: {
            handleSaveData(taskName, TaskNameMapToSteps[taskName].STARTED, true);
            const tourCategory = TutorialTourName.ONBOARDING_TUTORIAL_STEP;
            const preferences = [
                {
                    user_id: currentUserId,
                    category: tourCategory,
                    name: currentUserId,
                    value: OnboardingTourSteps.CHANNELS_AND_DIRECT_MESSAGES.toString(),
                },
                {
                    user_id: currentUserId,
                    category: tourCategory,
                    name: TTNameMapToATStatusKey[tourCategory],
                    value: AutoTourStatus.ENABLED.toString(),
                },
            ];
            dispatch(savePreferences(currentUserId, preferences));
            if (!inChannels) {
                dispatch(switchToChannels());
            }
            break;
        }
        case OnboardingTasksName.BOARDS_TOUR: {
            browserHistory.push('/boards');
            LocalStorageStore.setOnboardingTaskListShow(true);
            handleSaveData(taskName, TaskNameMapToSteps[taskName].FINISHED, true);
            break;
        }
        case OnboardingTasksName.PLAYBOOKS_TOUR: {
            browserHistory.push('/playbooks/start');
            LocalStorageStore.setOnboardingTaskListShow(true);
            handleSaveData(taskName, TaskNameMapToSteps[taskName].FINISHED, true);
            break;
        }
        case OnboardingTasksName.COMPLETE_YOUR_PROFILE: {
            dispatch(setStatusDropdown(true));
            dispatch(setShowOnboardingCompleteProfileTour(true));
            handleSaveData(taskName, TaskNameMapToSteps[taskName].STARTED, true);
            if (inAdminConsole) {
                dispatch(switchToChannels());
            }
            break;
        }
        case OnboardingTasksName.VISIT_SYSTEM_CONSOLE: {
            dispatch(setProductMenuSwitcherOpen(true));
            dispatch(setShowOnboardingVisitConsoleTour(true));
            handleSaveData(taskName, TaskNameMapToSteps[taskName].STARTED, true);
            break;
        }
        case OnboardingTasksName.INVITE_PEOPLE: {
            LocalStorageStore.setOnboardingTaskListShow(true);

            if (inAdminConsole) {
                dispatch(openInvitationsModal(1000));
            } else {
                dispatch(openInvitationsModal());
            }
            handleSaveData(taskName, TaskNameMapToSteps[taskName].FINISHED, true);
            break;
        }
        case OnboardingTasksName.DOWNLOAD_APP: {
            handleSaveData(taskName, TaskNameMapToSteps[taskName].FINISHED, true);
            const preferences = [{
                user_id: currentUserId,
                category: OnboardingTaskCategory,
                name: OnboardingTaskList.ONBOARDING_TASK_LIST_OPEN,
                value: 'true',
            }];
            dispatch(savePreferences(currentUserId, preferences));
            window.open('https://mattermost.com/download/', '_blank', 'noopener,noreferrer');
            break;
        }
        default:
        }
    };
};

